import React, { useState } from 'react'
import {
  Modal,
  Button,
  Typography,
  Icon,
  Empty,
  Alert,
  Spin,
  Tag,
  message as antdMessage
} from 'antd'
import jsPDF from 'jspdf'
import useVideoSuggestions from './useVideoSuggestions'

const { Title, Text, Paragraph } = Typography

const FIX_TYPE_META = {
  regenerate_same_image: {
    accent: '#f97316',
    background: '#fff7ed',
    borderColor: '#f59e0b',
    icon: 'reload',
    label: 'Regenerate clip',
    textColor: '#92400e'
  },
  image_prompt_suggestion: {
    accent: '#2563eb',
    background: '#eff6ff',
    borderColor: '#60a5fa',
    icon: 'picture',
    label: 'Image prompt update',
    textColor: '#1d4ed8'
  },
  video_prompt_suggestion: {
    accent: '#4f46e5',
    background: '#eef2ff',
    borderColor: '#818cf8',
    icon: 'video-camera',
    label: 'Video prompt update',
    textColor: '#3730a3'
  },
  negative_prompt_suggestion: {
    accent: '#dc2626',
    background: '#fef2f2',
    borderColor: '#f87171',
    icon: 'minus-circle',
    label: 'Negative prompt update',
    textColor: '#b91c1c'
  }
}

const toReadableFixType = fixType => {
  if (!fixType) {
    return 'Suggestion'
  }

  return fixType
    .replace(/_suggestion$/i, '')
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const getFixTypeMeta = fixType =>
  FIX_TYPE_META[fixType] || {
    accent: '#64748b',
    background: '#f8fafc',
    borderColor: '#94a3b8',
    icon: 'info-circle',
    label: toReadableFixType(fixType),
    textColor: '#334155'
  }

const pluralize = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`

const getSuggestionCountLabel = count =>
  pluralize(count, 'suggestion')

const formatSeconds = value => {
  const numericValue = Number(value)

  if (!Number.isFinite(numericValue)) {
    return null
  }

  const minutes = Math.floor(numericValue / 60)
  const seconds = (numericValue % 60).toFixed(1)
  const paddedSeconds = numericValue >= 60 ? seconds.padStart(4, '0') : seconds

  return minutes > 0 ? `${minutes}:${paddedSeconds}` : `${seconds}s`
}

const formatTimeWindow = suggestion => {
  const start = formatSeconds(suggestion?.error_time)
  const end = formatSeconds(suggestion?.error_end_time)

  if (start && end) {
    return `${start} - ${end}`
  }

  return start || end || 'Time not provided'
}

const renderSuggestionCard = (suggestion, index) => {
  const fixMeta = getFixTypeMeta(suggestion?.fix_type)

  return (
    <div
      key={`${suggestion?.fix_type || 'suggestion'}-${index}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '4px minmax(0, 1fr)',
        background: '#fff',
        border: '1px solid #dbe5f1',
        borderRadius: 14,
        boxShadow: '0 10px 30px rgba(15, 23, 42, 0.05)',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          background: fixMeta.accent
        }}
      />
      <div style={{ padding: '18px 20px' }}>
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            justifyContent: 'space-between',
            marginBottom: 14
          }}
        >
          <Tag
            style={{
              alignItems: 'center',
              background: fixMeta.background,
              border: `1px solid ${fixMeta.borderColor}`,
              borderRadius: 999,
              color: fixMeta.textColor,
              display: 'inline-flex',
              fontSize: 12,
              fontWeight: 700,
              lineHeight: '20px',
              marginRight: 0,
              maxWidth: '100%',
              padding: '2px 10px'
            }}
          >
            <Icon
              type={fixMeta.icon}
              style={{ fontSize: 12, marginRight: 6 }}
            />
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {fixMeta.label}
            </span>
          </Tag>
          <Text
            style={{
              alignItems: 'center',
              color: '#334155',
              display: 'inline-flex',
              flexShrink: 0,
              fontSize: 12,
              fontWeight: 600
            }}
          >
            <Icon type="clock-circle" style={{ marginRight: 6 }} />
            {formatTimeWindow(suggestion)}
          </Text>
        </div>

        <div style={{ marginBottom: 14 }}>
          <Text
            style={{
              color: '#64748b',
              display: 'block',
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 6
            }}
          >
            Issue
          </Text>
          <Text
            strong
            style={{
              color: '#111827',
              display: 'block',
              fontSize: 14,
              lineHeight: 1.55
            }}
          >
            {suggestion?.error_text || 'Potential issue detected'}
          </Text>
        </div>

        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: '12px 14px'
          }}
        >
          <Text
            style={{
              color: '#475569',
              display: 'block',
              fontSize: 11,
              fontWeight: 700,
              marginBottom: 4
            }}
          >
            Recommended fix
          </Text>
          <Paragraph
            style={{
              color: '#334155',
              fontSize: 13,
              lineHeight: 1.6,
              margin: 0
            }}
          >
            {suggestion?.fix_suggestion_text ||
              'No suggested fix was returned.'}
          </Paragraph>
        </div>
      </div>
    </div>
  )
}

const renderReviewSummary = ({
  reviewedSceneCount,
  reviewedShotCount,
  suggestionEntries,
  totalSuggestions
}) => (
  <div
    style={{
      alignItems: 'center',
      background: '#ffffff',
      border: '1px solid #dbe5f1',
      borderRadius: 16,
      boxShadow: '0 14px 36px rgba(15, 23, 42, 0.06)',
      display: 'grid',
      gap: 16,
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      marginBottom: 26,
      padding: '18px 20px'
    }}
  >
    <div>
      <Text
        style={{
          color: '#64748b',
          display: 'block',
          fontSize: 12,
          fontWeight: 700,
          marginBottom: 6
        }}
      >
        Review summary
      </Text>
      <Title
        level={4}
        style={{
          color: '#0f172a',
          fontSize: 20,
          lineHeight: 1.25,
          margin: 0
        }}
      >
        {pluralize(totalSuggestions, 'issue')} found
      </Title>
      <Text style={{ color: '#64748b', display: 'block', marginTop: 4 }}>
        Across {pluralize(reviewedShotCount || suggestionEntries.length, 'shot')}
      </Text>
    </div>

    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'flex-end'
      }}
    >
      <Tag
        style={{
          alignItems: 'center',
          background: '#eff6ff',
          border: '1px solid #93c5fd',
          borderRadius: 999,
          color: '#1d4ed8',
          display: 'inline-flex',
          fontWeight: 700,
          lineHeight: '22px',
          marginRight: 0,
          padding: '3px 12px'
        }}
      >
        <Icon type="appstore" style={{ marginRight: 6 }} />
        {pluralize(reviewedSceneCount, 'scene')} reviewed
      </Tag>
      <Tag
        style={{
          alignItems: 'center',
          background: '#f8fafc',
          border: '1px solid #cbd5e1',
          borderRadius: 999,
          color: '#334155',
          display: 'inline-flex',
          fontWeight: 700,
          lineHeight: '22px',
          marginRight: 0,
          padding: '3px 12px'
        }}
      >
        <Icon type="file-search" style={{ marginRight: 6 }} />
        {pluralize(suggestionEntries.length, 'shot')} with notes
      </Tag>
    </div>
  </div>
)

const renderShotSection = ([shotLabel, shotSuggestions], index) => {
  const suggestionsForShot = Array.isArray(shotSuggestions)
    ? shotSuggestions
    : []

  return (
    <div
      key={shotLabel}
      style={{
        borderTop: index > 0 ? '1px solid #dfe7f1' : 'none',
        paddingTop: index > 0 ? 22 : 0,
        marginBottom: 24
      }}
    >
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          gap: 12,
          justifyContent: 'space-between',
          marginBottom: 12
        }}
      >
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            gap: 10,
            minWidth: 0
          }}
        >
          <div
            style={{
              alignItems: 'center',
              background: '#e0ecff',
              border: '1px solid #b9d4ff',
              borderRadius: 10,
              color: '#1d4ed8',
              display: 'flex',
              flexShrink: 0,
              fontSize: 13,
              height: 32,
              justifyContent: 'center',
              width: 32
            }}
          >
            <Icon type="video-camera" />
          </div>
          <div style={{ minWidth: 0 }}>
            <Text
              strong
              style={{
                color: '#0f172a',
                display: 'block',
                fontSize: 15,
                lineHeight: 1.35,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {shotLabel}
            </Text>
            <Text style={{ color: '#64748b', fontSize: 12 }}>
              {getSuggestionCountLabel(suggestionsForShot.length)}
            </Text>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        {suggestionsForShot.map(renderSuggestionCard)}
      </div>
    </div>
  )
}

const sanitizeFileName = value =>
  String(value || 'video')
    .trim()
    .replace(/[^a-z0-9_-]+/gi, '_')
    .replace(/^_+|_+$/g, '') || 'video'

const addPdfText = ({
  pdf,
  cursor,
  text,
  fontSize = 10,
  fontStyle = 'normal',
  lineHeight = 14,
  spacingAfter = 6,
  textColor = [51, 65, 85],
  x = cursor.margin,
  maxWidth = cursor.contentWidth
}) => {
  pdf.setFont('helvetica', fontStyle)
  pdf.setFontSize(fontSize)
  pdf.setTextColor(...textColor)

  const lines = pdf.splitTextToSize(String(text || ''), maxWidth)

  lines.forEach(line => {
    if (cursor.y + lineHeight > cursor.pageHeight - cursor.margin) {
      pdf.addPage()
      cursor.y = cursor.margin
    }

    pdf.text(line, x, cursor.y)
    cursor.y += lineHeight
  })

  cursor.y += spacingAfter
}

const downloadReviewPdf = ({
  reviewedSceneCount,
  reviewedShotCount,
  suggestionEntries,
  totalSuggestions,
  videoId
}) => {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' })
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const cursor = {
    contentWidth: pageWidth - 88,
    margin: 44,
    pageHeight,
    pageWidth,
    y: 48
  }
  const generatedAt = new Date().toLocaleString()

  pdf.setProperties({ title: 'AI Review' })

  addPdfText({
    pdf,
    cursor,
    text: 'AI Review',
    fontSize: 22,
    fontStyle: 'bold',
    lineHeight: 26,
    spacingAfter: 4,
    textColor: [15, 23, 42]
  })
  addPdfText({
    pdf,
    cursor,
    text: 'Final video quality notes and suggested fixes',
    fontSize: 11,
    lineHeight: 15,
    spacingAfter: 18,
    textColor: [100, 116, 139]
  })

  pdf.setDrawColor(203, 213, 225)
  pdf.line(cursor.margin, cursor.y, pageWidth - cursor.margin, cursor.y)
  cursor.y += 22

  addPdfText({
    pdf,
    cursor,
    text: `${pluralize(totalSuggestions, 'issue')} found`,
    fontSize: 14,
    fontStyle: 'bold',
    lineHeight: 18,
    spacingAfter: 4,
    textColor: [15, 23, 42]
  })
  addPdfText({
    pdf,
    cursor,
    text: `Reviewed ${pluralize(reviewedSceneCount, 'scene')} across ${pluralize(
      reviewedShotCount || suggestionEntries.length,
      'shot'
    )}. ${pluralize(suggestionEntries.length, 'shot')} include review notes.`,
    fontSize: 10,
    lineHeight: 14,
    spacingAfter: 4
  })
  addPdfText({
    pdf,
    cursor,
    text: `Generated: ${generatedAt}`,
    fontSize: 9,
    lineHeight: 13,
    spacingAfter: 18,
    textColor: [100, 116, 139]
  })

  suggestionEntries.forEach(([shotLabel, shotSuggestions], shotIndex) => {
    const suggestionsForShot = Array.isArray(shotSuggestions)
      ? shotSuggestions
      : []

    if (cursor.y + 44 > cursor.pageHeight - cursor.margin) {
      pdf.addPage()
      cursor.y = cursor.margin
    }

    addPdfText({
      pdf,
      cursor,
      text: `${shotIndex + 1}. ${shotLabel}`,
      fontSize: 13,
      fontStyle: 'bold',
      lineHeight: 17,
      spacingAfter: 2,
      textColor: [15, 23, 42]
    })
    addPdfText({
      pdf,
      cursor,
      text: getSuggestionCountLabel(suggestionsForShot.length),
      fontSize: 9,
      lineHeight: 12,
      spacingAfter: 8,
      textColor: [100, 116, 139]
    })

    suggestionsForShot.forEach((suggestion, suggestionIndex) => {
      const fixMeta = getFixTypeMeta(suggestion?.fix_type)

      addPdfText({
        pdf,
        cursor,
        text: `${suggestionIndex + 1}. ${fixMeta.label} | ${formatTimeWindow(
          suggestion
        )}`,
        fontSize: 10,
        fontStyle: 'bold',
        lineHeight: 14,
        spacingAfter: 4,
        textColor: [30, 64, 175]
      })
      addPdfText({
        pdf,
        cursor,
        text: `Issue: ${suggestion?.error_text || 'Potential issue detected'}`,
        fontSize: 10,
        lineHeight: 14,
        spacingAfter: 4
      })
      addPdfText({
        pdf,
        cursor,
        text: `Recommended fix: ${
          suggestion?.fix_suggestion_text || 'No suggested fix was returned.'
        }`,
        fontSize: 10,
        lineHeight: 14,
        spacingAfter: 12
      })
    })

    cursor.y += 4
  })

  pdf.save(
    `AI_Review_${sanitizeFileName(videoId)}_${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`
  )
}

const VideoSuggestionsModal = ({
  visible,
  onCancel,
  videoUrl,
  shotList,
  videoId,
  storedReview,
  setFormData
}) => {
  const [isDownloadingReview, setIsDownloadingReview] = useState(false)
  const {
    suggestions,
    isLoading,
    error,
    fetchSuggestions,
    suggestionRequest,
    hasStartedReview,
    startReview,
    progress,
    message
  } = useVideoSuggestions({
    visible,
    videoUrl,
    shotList,
    videoId,
    storedReview,
    setFormData
  })

  const suggestionsByShot = suggestions?.suggestions_by_shot || {}
  const suggestionEntries = Object.entries(suggestionsByShot)
  const totalSuggestions = suggestions?.total_suggestions || 0
  const reviewedSceneCount = suggestionRequest?.sceneCount || 0
  const reviewedShotCount =
    suggestionRequest?.shotCount || suggestionEntries.length
  const hasRequestPayload = Boolean(suggestionRequest?.payload)
  const shouldShowIntro = hasRequestPayload && !hasStartedReview && !isLoading
  const canDownloadReview =
    hasRequestPayload &&
    hasStartedReview &&
    !isLoading &&
    !error &&
    suggestionEntries.length > 0

  const handleDownloadReview = () => {
    if (!canDownloadReview) {
      antdMessage.warning('No review notes available to download.')
      return
    }

    setIsDownloadingReview(true)

    try {
      downloadReviewPdf({
        reviewedSceneCount,
        reviewedShotCount,
        suggestionEntries,
        totalSuggestions,
        videoId
      })
      antdMessage.success('Review PDF downloaded.')
    } catch (downloadError) {
      console.error('Failed to download AI review PDF:', downloadError)
      antdMessage.error('Failed to download review PDF.')
    } finally {
      setIsDownloadingReview(false)
    }
  }

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      onCancel={onCancel}
      width={960}
      centered
      destroyOnClose
      className="video-suggestions-modal"
      title={
        <div style={{ alignItems: 'center', display: 'flex', gap: 14 }}>
          <div
            style={{
              alignItems: 'center',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 12,
              color: '#1d4ed8',
              display: 'flex',
              height: 40,
              justifyContent: 'center',
              width: 40
            }}
          >
            <Icon type="bulb" style={{ fontSize: 18 }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <Title
              level={4}
              style={{
                color: '#0f172a',
                fontSize: 20,
                lineHeight: 1.25,
                margin: 0
              }}
            >
              AI Review
            </Title>
            <Text style={{ color: '#64748b', fontSize: 13 }}>
              Final video quality notes and suggested fixes
            </Text>
          </div>
        </div>
      }
      footer={[
        canDownloadReview && (
          <Button
            key="download"
            onClick={handleDownloadReview}
            loading={isDownloadingReview}
            style={{
              borderRadius: 8,
              fontWeight: 700,
              height: 40,
              minWidth: 148
            }}
          >
            <Icon type="download" />
            Download Review
          </Button>
        ),
        <Button
          key="close"
          onClick={onCancel}
          style={{
            borderRadius: 8,
            fontWeight: 700,
            height: 40,
            minWidth: 108
          }}
        >
          Close
        </Button>,
        shouldShowIntro ? (
          <Button
            key="start"
            type="primary"
            onClick={startReview}
            disabled={!hasRequestPayload}
            style={{
              borderRadius: 8,
              fontWeight: 700,
              height: 40,
              minWidth: 156
            }}
          >
            Generate Review
          </Button>
        ) : (
          <Button
            key="refresh"
            type="primary"
            ghost
            onClick={fetchSuggestions}
            disabled={!hasRequestPayload}
            loading={isLoading}
            style={{
              borderRadius: 8,
              fontWeight: 700,
              height: 40,
              minWidth: 160
            }}
          >
            Refresh Review
          </Button>
        )
      ]}
      bodyStyle={{
        background: '#f5f7fb',
        maxHeight: 'calc(100vh - 220px)',
        minHeight: 300,
        overflowX: 'hidden',
        overflowY: 'auto'
      }}
    >
      {!hasRequestPayload && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span>
              Review needs the final video, shot timing data, and the source
              generation video ID.
            </span>
          }
        />
      )}

      {shouldShowIntro && (
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #dbe5f1',
            borderRadius: 16,
            margin: '12px auto',
            maxWidth: 520,
            padding: '34px 32px',
            textAlign: 'center'
          }}
        >
          <Title
            level={4}
            style={{ marginTop: 0, marginBottom: 10, color: '#172033' }}
          >
            Generate AI review now?
          </Title>
          <Paragraph
            style={{
              margin: 0,
              color: '#5b6475',
              fontSize: 14,
              lineHeight: 1.7
            }}
          >
            We will review the current final video and return AI suggestions for
            any issues found. Would you like to continue?
          </Paragraph>
        </div>
      )}

      {hasRequestPayload && isLoading && (
        <div
          style={{
            alignItems: 'center',
            background: '#ffffff',
            border: '1px solid #dbe5f1',
            borderRadius: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            justifyContent: 'center',
            minHeight: 260,
            textAlign: 'center'
          }}
        >
          <Spin size="large" tip="Generating review..." />
          {!!message && (
            <Text type="secondary" style={{ maxWidth: 320 }}>
              {message}
            </Text>
          )}
          {typeof progress === 'number' && progress > 0 && (
            <Text type="secondary">{progress}% complete</Text>
          )}
        </div>
      )}

      {hasRequestPayload && !isLoading && error && (
        <Alert
          type="error"
          showIcon
          message="Review failed"
          description={error}
          style={{ borderRadius: 12, borderColor: '#fecaca' }}
        />
      )}

      {hasRequestPayload &&
        hasStartedReview &&
        !isLoading &&
        !error &&
        suggestionEntries.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>No issues were flagged in this review pass.</span>
            }
          />
        )}

      {hasRequestPayload &&
        hasStartedReview &&
        !isLoading &&
        !error &&
        suggestionEntries.length > 0 && (
          <div>
            {renderReviewSummary({
              reviewedSceneCount,
              reviewedShotCount,
              suggestionEntries,
              totalSuggestions
            })}

            {suggestionEntries.map(renderShotSection)}
          </div>
        )}
    </Modal>
  )
}

export default VideoSuggestionsModal
